/**
 * What a PERSONAL tenant is allowed to be on this deployment — the ONE
 * implementation of a two-layer rule, in the dependency-free package because
 * the API, the workers, the console and the SPA must all answer it identically.
 *
 * Design of record: docs/plans/2026-08-11_IDENTITY_ONLY_TENANT_MODE.md.
 * Build plan: docs/plans/2026-08-11_IDENTITY_ONLY_AMENDMENT_PLAN.md §3.1.
 *
 * ── The two modes ───────────────────────────────────────────────────────
 *
 *   'full'          the current product — personal Drive and build surfaces.
 *   'identity-only' the tenant remains the person's stable identity and
 *                   acting-tenant anchor, but is not a workspace and not a
 *                   storage destination.
 *
 * `identity-only` is NOT an account type and NOT a lockout. The person still
 * signs in, keeps their account, switches into organizations, receives
 * notifications, and uses grant-authorized apps and shares owned by `full`
 * tenants. What they lose is their own Drive as a place to read from and write
 * to. Everything else about them keeps working, which is why the mode is named
 * after what it PRESERVES rather than after what it removes.
 *
 * ── REACHABILITY, not existence — why nothing here is destructive ───────
 *
 * Every user HAS a personal tenant: `users.personal_tenant_id` is NOT NULL,
 * provisioned at signup, and a great deal of auth and share-session machinery
 * hangs off it. This rule decides what that tenant may DO, never whether it
 * exists. Nothing deletes, archives or declines to provision anything — which
 * is what makes a mode switch reversible in both directions: set it back to
 * `full` and every file, version, blob and root is exactly where its owner left
 * it, with no repair or rematerialization step.
 *
 * ── The chain ───────────────────────────────────────────────────────────
 *
 *   tenants.personal_mode_override                          per-tenant,  NULL = inherit
 *     → platform_settings.default_personal_tenant_mode   deployment, NULL = unchosen
 *       → DEFAULT_TENANT_MODE                            the shipped 'full'
 *
 * Both `NULL`s are meaningful and they mean DIFFERENT things — "inherit the
 * deployment" and "this deployment has not chosen" — which is why the third
 * state is spelled as an absent value at every boundary rather than as a third
 * member of the union. The product model's third state is `undefined`;
 * PostgreSQL and JSON cannot carry that, so DB `NULL`, response `null` and an
 * absent patch key all stand in for it (design of record §3.1).
 *
 * There is deliberately **no `brand.yaml` link**, unlike the landing surface's
 * otherwise-identical-looking chain. A brand default would be a build-time
 * answer to a question an operator asks at runtime.
 *
 * ── Organizations are ALWAYS `full` ─────────────────────────────────────
 *
 * The mode applies to personal tenants only. `resolveTenantModeForType` below
 * is where that is decided, and it fails safe to `full` for any tenant whose
 * type is not exactly `'personal'`. See its docblock for why the fail-safe runs
 * in the permissive direction here when it runs the other way almost everywhere
 * else in this codebase.
 *
 * ── There is no boolean and no negation anywhere in this chain ──────────
 *
 * The value is a MODE, not an "enabled?" flag, so no caller negates it and no
 * two callers can disagree about polarity. Keep it that way: a
 * `isIdentityOnly()` helper reads harmlessly and is the first step towards a
 * gate that fails open while looking like it fails closed. Compare against the
 * named value at the point of use.
 */

/** Every valid mode, for route validation and exhaustive UI lists. */
export const TENANT_MODES = ['full', 'identity-only'] as const

export type TenantMode = (typeof TENANT_MODES)[number]

/**
 * What an unchosen deployment means: the current product, unchanged.
 *
 * This is the property the migration rests on. Both columns are `NULL` on every
 * existing deployment and every existing tenant, so every one of them resolves
 * `full` through this constant and nothing moves until an operator acts.
 */
export const DEFAULT_TENANT_MODE: TenantMode = 'full'

/**
 * Narrow unknown wire/column input to the closed union.
 *
 * A type guard rather than a comparison at each call site, for the same reason
 * `isLandingExperience` is one: every caller is validating a value from OUTSIDE
 * the type system — a `text` column, a JSON body, an HTTP response — and the
 * call sites that forget look no different from the ones that do not.
 */
export function isTenantMode(value: unknown): value is TenantMode {
  return value === 'full' || value === 'identity-only'
}

/**
 * Resolve a PERSONAL tenant's effective mode from its override and the
 * deployment default. Pure; the row fetch is `resolveEffectiveTenantMode`'s job
 * (`@papan/database`), which is also where the organization short-circuit and
 * the tenant-type read live.
 *
 * ── Why this narrows instead of `override ?? platformDefault ?? 'full'` ──
 *
 * The design of record writes the `??` form against parameters typed
 * `TenantMode | null | undefined`, and for well-formed input the two are
 * identical. They diverge on exactly the input this function actually receives:
 * a `text` column, a raw `pool.query` row, a restored dump, a hand-run UPDATE.
 * Drizzle's `$type<TenantMode>()` ASSERTS the column's type without checking it,
 * so TypeScript will happily hand this a string that is not a mode — and the
 * `??` form would return it, making the `TenantMode` return type a lie and
 * putting an unrecognised value into a comparison that decides access.
 *
 * Narrowing instead means an unrecognised value degrades to INHERIT — it falls
 * through to the next link exactly as an absent value does. That is the only
 * behaviour that stays correct under a value nobody anticipated, and the DB
 * CHECK already makes it unreachable in practice. The parameters are therefore
 * typed as loosely as their real sources (`string | null | undefined`) rather
 * than pretending the boundary was already crossed.
 *
 * The degradation is by explicit narrowing, NOT by construction — if you
 * restructure this, keep `isTenantMode` on both links. Its predecessor's
 * `filter(isPersonalTenantOverride)` was a provable no-op that read as
 * validation, and mutation-checking is what found it.
 */
export function resolvePersonalTenantMode(
  tenantOverride: string | null | undefined,
  platformDefault: string | null | undefined,
): TenantMode {
  if (isTenantMode(tenantOverride)) return tenantOverride
  if (isTenantMode(platformDefault)) return platformDefault
  return DEFAULT_TENANT_MODE
}

/**
 * The same rule with the tenant's TYPE in front of it — the whole answer for a
 * tenant row you already hold.
 *
 * Two callers need this and neither can use the other's: the DB-level
 * `resolveEffectiveTenantMode` (one tenant, fetched), and the console's
 * customers LIST (a page of tenant rows from one raw query, where calling a
 * per-tenant async reader would be N round trips for a screen). Both must give
 * the same answer, so the rule is here rather than written twice.
 *
 * ── Organizations short-circuit, and the fail-safe is PERMISSIVE ────────
 *
 * `personal_mode_override` is refused on an organization by the write route, and the
 * column is inert there — but a hand-run UPDATE, a restored dump or a future
 * bug could still put a value on one. Anything whose type is not exactly
 * `'personal'` therefore resolves `full` before the override is even read.
 *
 * This is deliberately the opposite direction from most fail-safes in this
 * codebase, and the reason is asymmetric damage. An unknown tenant type
 * resolving `identity-only` would freeze an entire ORGANIZATION's drive — every
 * member, every file, on a data glitch. Resolving `full` leaves one personal
 * drive reachable that perhaps should not be, which is visible, recoverable,
 * and costs nobody their work. The design of record states the same conclusion
 * (§3.2: "the resolver fails safe to `full` for old or malformed organization
 * rows").
 *
 * Note what this does NOT do: it is not a permission check and not an
 * authorization boundary. It answers "what mode is this tenant in", and the
 * guards that consume it (`assertTenantWritable` and friends) supplement
 * permission checks rather than replacing them.
 */
export function resolveTenantModeForType(
  tenantType: string | null | undefined,
  tenantOverride: string | null | undefined,
  platformDefault: string | null | undefined,
): TenantMode {
  if (tenantType !== 'personal') return DEFAULT_TENANT_MODE
  return resolvePersonalTenantMode(tenantOverride, platformDefault)
}

/**
 * Whether this deployment's DEFAULT gives personal tenants a Drive — the signal
 * for deployment-wide UI CHROME (the console's Personal/Organizations filter is
 * hidden when it does not). Deliberately NOT a per-tenant decision: an
 * individual tenant may still override in either direction, and the guards do
 * that with the resolvers above. Lives here (the sanctioned reader) so callers
 * name this intent instead of comparing the raw column — see
 * `no-raw-tenant-mode-reads.test.ts`.
 */
export function deploymentDefaultAllowsPersonalDrives(
  platformDefault: string | null | undefined,
): boolean {
  return resolvePersonalTenantMode(null, platformDefault) === DEFAULT_TENANT_MODE
}

/**
 * Which link decided a PERSONAL tenant's mode — for the console, which must say
 * WHY a mode is in force rather than render a control beside a live behaviour it
 * cannot account for.
 */
export type TenantModeSource = 'tenant' | 'platform' | 'default'

/**
 * Which link decided the DEPLOYMENT's default. A narrower question with a
 * narrower answer: there is no tenant in play, so `'tenant'` is not among the
 * possibilities.
 *
 * Two unions rather than one, because the deployment-wide screen physically
 * cannot report a per-tenant source and a shared union would let it try. The
 * previous model had a single three-value union serving both screens and it was
 * wrong for each of them in a different way.
 */
export type PlatformModeSource = 'setting' | 'default'

/**
 * Derived from the SAME inputs and the SAME narrowing as
 * `resolvePersonalTenantMode`, so a label can never disagree with the value it
 * labels. Keep the two functions' conditions in step: if the resolver gains a
 * link, this gains a case, in the same edit.
 */
export function tenantModeSource(
  tenantOverride: string | null | undefined,
  platformDefault: string | null | undefined,
): TenantModeSource {
  if (isTenantMode(tenantOverride)) return 'tenant'
  if (isTenantMode(platformDefault)) return 'platform'
  return 'default'
}

/** The deployment-wide sibling of `tenantModeSource`. */
export function platformModeSource(
  platformDefault: string | null | undefined,
): PlatformModeSource {
  return isTenantMode(platformDefault) ? 'setting' : 'default'
}
